// Fixes cached layouts that were generated under a different protocol or www/non-www variant.
// Layout cache (or third-party caches) may store absolute image/video URLs with HTTP or without www,
// then serve that markup to users on HTTPS or www. Browsers block mixed-content images (or Cloudflare/CDNs redirect),
// so this script rewrites only same-site absolute URLs to match the visitor's current origin on every `newpageshown`.

import { getScopeRoot, getScopeForIsProjectOverlay } from '../scope/scope'

const currentHost = window.location.hostname
const normalizedCurrentHost = currentHost.replace(/^www\./i, '')
const currentProtocol = window.location.protocol
const currentOrigin = window.location.origin

const DATA_URL_PATTERN = /^data:/i
const SKIP_PROTOCOLS = /^(?:data|blob|javascript|mailto|tel):/i

const SELECTORS = 'img, source, picture img, picture source, video, audio, link[rel="preload"][as="image"], link[rel="preload"][as="video"], link[rel="preload"][as="audio"]'

var normalizeAllImages = function (root) {
	const scope = root && typeof root !== 'undefined' ? (root instanceof Element ? root : document) : document

	const elements = scope.querySelectorAll(SELECTORS)
	if (!elements || elements.length === 0) {
		return
	}

	elements.forEach(function (el) {
		normalizeElementAttributes(el)
	})

	normalizeBackgroundImages(scope)
}

var normalizeElementAttributes = function (el) {
	rewriteAttribute(el, 'src')
	rewriteAttribute(el, 'poster')
	rewriteAttribute(el, 'data-src')
	rewriteAttribute(el, 'data-lay-src') // carousel / modules
	rewriteAttribute(el, 'data-mp4') // thumbnail videos loaded on first mouseover

	rewriteSrcset(el, 'srcset')
	rewriteSrcset(el, 'data-srcset')
	rewriteSrcset(el, 'data-lay-srcset')
}

var rewriteAttribute = function (el, attr) {
	if (!el || !attr || !el.hasAttribute(attr)) {
		return
	}
	const value = el.getAttribute(attr)
	const newValue = normalizeUrl(value)
	if (newValue && newValue !== value) {
		el.setAttribute(attr, newValue)
	}
}

var rewriteSrcset = function (el, attr) {
	if (!el || !attr || !el.hasAttribute(attr)) {
		return
	}
	const value = el.getAttribute(attr)
	if (!value || typeof value !== 'string') {
		return
	}

	const parts = value.split(',')
	let changed = false
	const rewritten = parts
		.map(function (part) {
			const trimmed = part.trim()
			if (trimmed === '') {
				return trimmed
			}
			const segments = trimmed.split(/\s+/)
			const url = segments.shift()
			const newUrl = normalizeUrl(url)
			if (newUrl && newUrl !== url) {
				changed = true
				return newUrl + (segments.length ? ' ' + segments.join(' ') : '')
			}
			return trimmed
		})
		.join(', ')

	if (changed) {
		el.setAttribute(attr, rewritten)
	}
}

var normalizeBackgroundImages = function (scope) {
	if (!scope || typeof scope.querySelectorAll !== 'function') {
		return
	}
	const elements = scope.querySelectorAll('[style*="background-image"]')
	if (!elements || elements.length === 0) {
		return
	}

	elements.forEach(function (el) {
		const style = el.getAttribute('style')
		if (!style || style.indexOf('url(') === -1) {
			return
		}
		const rewritten = style.replace(/url\((['"]?)([^)]+?)\1\)/gi, function (match, quote, url) {
			const cleaned = url.trim()
			const newUrl = normalizeUrl(cleaned)
			if (newUrl && newUrl !== cleaned) {
				const q = quote || ''
				return 'url(' + q + newUrl + q + ')'
			}
			return match
		})
		if (rewritten !== style) {
			el.setAttribute('style', rewritten)
		}
	})
}

var normalizeUrl = function (url) {
	if (!url || typeof url !== 'string') {
		return null
	}
	const trimmed = url.trim()
	if (trimmed === '' || SKIP_PROTOCOLS.test(trimmed) || trimmed.indexOf('#') === 0) {
		return null
	}

	// ignore relative URLs - caching issues happen with absolute URLs that include protocol/host
	if (!startsWithHttp(trimmed) && !trimmed.startsWith('//')) {
		return null
	}

	let parsed
	try {
		parsed = new URL(trimmed, currentOrigin)
	} catch (e) {
		return null
	}

	if (parsed.origin === currentOrigin) {
		return null // already matches
	}

	// Ignore if different host (likely CDN or third-party)
	const normalizedTargetHost = parsed.hostname.replace(/^www\./i, '')
	if (normalizedTargetHost !== normalizedCurrentHost) {
		return null
	}

	let updated = false

	if (parsed.protocol !== currentProtocol) {
		parsed.protocol = currentProtocol
		updated = true
	}

	if (parsed.host !== window.location.host) {
		parsed.host = window.location.host
		updated = true
	}

	if (!updated) {
		return null
	}

	// Preserve original format (protocol-relative vs absolute)
	if (trimmed.startsWith('//')) {
		return '//' + parsed.host + parsed.pathname + parsed.search + parsed.hash
	}

	return parsed.href
}

var startsWithHttp = function (value) {
	return /^https?:/i.test(value)
}

var handleNewPage = function (layoutObj, type, obj, isProjectOverlayObj) {
	// Scope to the .lay-content root for the current event so we don't re-process
	// the other scope's markup. normalizeUrl is also idempotent (already-matching
	// origins return null), so the work is wasted at worst — but the scope keeps
	// behavior predictable when newpageshown fires for both overlay and underlay.
	const scope = getScopeRoot(getScopeForIsProjectOverlay(isProjectOverlayObj)) || document
	normalizeAllImages(scope)
}

if (window && window.laytheme && typeof window.laytheme.on === 'function') {
	window.laytheme.on('newpageshown', handleNewPage)
}
